import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { ApplicationSettings, ApplicationSettingService, HttpService, IdDescription } from 'tms-lib';

/**
 * Provides the data the inline label-selection panel needs: whether the feature is switched
 * on (the ShowLabelSelection application setting, OFF by default) and which labels the
 * signed-in user's roles grant. Both are cached once per session.
 *
 * Only the auto-print screens (PO receipt, cutting, picking) use this - via the panel.
 * Manual/icon prints and bulk grid prints go straight to PrintService as before.
 */
@Injectable({
  providedIn: 'root',
})
export class LabelSelectionService {
  private readonly httpService = inject(HttpService);
  private readonly applicationSettingService = inject(ApplicationSettingService);

  private showLabelSelection$: Observable<boolean>;
  private permittedLabelNames$: Observable<string[]>;

  /** The ShowLabelSelection feature toggle. Cached; defaults to OFF on error/absent. */
  getShowLabelSelection(): Observable<boolean> {
    this.showLabelSelection$ ??= this.applicationSettingService.getBoolean(ApplicationSettings.showLabelSelection).pipe(
      catchError(() => of(false)),
      shareReplay(1),
    );

    return this.showLabelSelection$;
  }

  /** The label document Descriptions the user's roles grant. Cached; empty on error. */
  getPermittedLabelNames(): Observable<string[]> {
    // No 'api/' prefix - HttpService prepends configService.config.apiUrl, which already
    // ends in /api/.
    this.permittedLabelNames$ ??= this.httpService.get<IdDescription[]>('TmsWeb/Document/UserLabels').pipe(
      map((labels: IdDescription[]) => labels.map((label) => label.description)),
      catchError(() => of<string[]>([])),
      shareReplay(1),
    );

    return this.permittedLabelNames$;
  }

  /**
   * Maps a template file name to its Document Description, e.g. 'customer-stock-label' ->
   * 'Customer Stock Label'. Kept in step with the ReportNames enum; PrintService adds any
   * '-medium' suffix later, so the name here is always the base one.
   */
  labelName(template: string): string {
    return template
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}
